// lib/takeoff/specParse.js — a project manual (spec book) PDF → sections → searchable chunks. No AI, runs in the
// browser (the server never holds the PDF), so a 300-page manual costs the dyno nothing.
//
//   const pages = await readSpecPages(pdfDoc, onProgress)      // pdfjs document → [{ page, header[], lines[] }]
//   const { sections, chunks } = splitSpec(pages)               // CSI sections + article-sized chunks
//   const index = buildSpecIndex(chunks); searchSpec(index, "5/8 type x", { sections: Set })
//
// Sections are found from the page header ("SECTION 09 21 16 / GYPSUM BOARD ASSEMBLIES" — on nearly every manual),
// else from "SECTION 09 21 16" at the top of a page. Headers / footers repeated on many pages are dropped.

import { _internals as itemSearchInternals } from "./itemSearch";

const { analyzeText, analyzeQuery } = itemSearchInternals;

// ── drywall-trade relevance ──────────────────────────────────────────────────
// core = we price it; related = read it (coordination, by others, finishes over our work)
const CORE = [
  /^09 2[1-9]/, // gypsum board, framing, plaster, veneer
  /^05 4[0-9]/, // cold-formed metal framing
  /^07 2[1-2]/, // thermal / acoustic insulation
  /^07 84/, // firestopping
  /^09 5[1-4]/, // acoustical ceilings
  /^09 84/, // sound-absorbing wall & ceiling units
  /^08 31/, // access doors and panels
];
const RELATED = [
  /^06 1[0-6]/, // rough carpentry / blocking / sheathing
  /^07 92/, // joint sealants (acoustic sealant)
  /^07 81/, // fire-resistive coatings
  /^08 1[1-4]/, // doors & frames (frames set in our walls)
  /^09 30/, // tiling (cement board / substrate)
  /^09 9/, // painting (finish level)
  /^10 2[1-2]/, // partitions / operable walls (headers, backing)
  /^01 /, // general requirements
];
const CORE_TITLE = /gypsum|drywall|plaster|metal framing|steel stud|ceiling|bulkhead|soffit|shaft ?wall|insulation|firestop|acoustic(al)? panel|access (door|panel)/i;

export function sectionTrade(num, title) {
  const n = String(num || "");
  if (CORE.some((r) => r.test(n)) || CORE_TITLE.test(title || "")) return "core";
  if (RELATED.some((r) => r.test(n))) return "related";
  return "";
}

// ── pdf → lines ──────────────────────────────────────────────────────────────
// lines are rebuilt from text items by their y; each keeps the x of its first item (0..1 of the page width)
function linesOf(items, viewport) {
  const H = viewport.height;
  const W = viewport.width;
  const rows = [];
  for (const it of items) {
    const s = it.str;
    if (!s || !s.trim()) continue;
    const x = it.transform[4];
    const y = H - it.transform[5]; // top-down
    let row = rows.find((r) => Math.abs(r.y - y) < 2.5);
    if (!row) rows.push((row = { y, items: [] }));
    row.items.push({ x, s, w: it.width || 0 });
  }
  rows.sort((a, b) => a.y - b.y);
  return rows.map((r) => {
    r.items.sort((a, b) => a.x - b.x);
    let text = "";
    let end = null;
    const parts = []; // runs split at big gaps (header left / header right)
    let cur = null;
    for (const it of r.items) {
      const gap = end == null ? 0 : it.x - end;
      if (!cur || gap > W * 0.08) parts.push((cur = { x: it.x / W, s: "" }));
      cur.s += (cur.s && gap > 1.5 && !/\s$/.test(cur.s) ? " " : "") + it.s;
      text += (text && gap > 1.5 && !/\s$/.test(text) ? " " : "") + it.s;
      end = it.x + it.w;
    }
    return { y: r.y / H, x: r.items[0].x / W, text: text.replace(/\s+/g, " ").trim(), parts };
  });
}

/** pdfjs document → [{ page, header: [line], body: [line], footer: [line] }] */
export async function readSpecPages(pdf, onProgress) {
  const out = [];
  for (let p = 1; p <= pdf.numPages; p++) {
    const page = await pdf.getPage(p);
    const viewport = page.getViewport({ scale: 1 });
    const tc = await page.getTextContent();
    const lines = linesOf(tc.items, viewport);
    out.push({
      page: p,
      header: lines.filter((l) => l.y < 0.13),
      body: lines.filter((l) => l.y >= 0.13 && l.y <= 0.93),
      footer: lines.filter((l) => l.y > 0.93),
    });
    page.cleanup();
    onProgress?.(p, pdf.numPages);
  }
  return out;
}

const SEC_RE = /SECTION\s+(\d{2})\s?(\d{2})\s?(\d{2})(?:\.(\d{1,2}))?\b/i;
const secNum = (m) => `${m[1]} ${m[2]} ${m[3]}${m[4] ? `.${m[4]}` : ""}`;

function headerSection(header) {
  for (let i = 0; i < header.length; i++) {
    const m = header[i].text.match(SEC_RE);
    if (!m) continue;
    // the title: the right-hand run of the next header line (or the rest of this one)
    const after = header[i].text.slice(header[i].text.search(SEC_RE) + m[0].length).trim();
    let title = after;
    if (!title) {
      // right-hand runs of the header lines below it (a long title wraps onto a second line)
      const right = [];
      for (const nx of header.slice(i + 1, i + 4)) {
        const r = nx.parts.filter((p) => p.x > 0.45).map((p) => p.s.trim()).join(" ");
        if (!r || /^(page|issued|project|date|rev)/i.test(r)) break;
        right.push(r);
      }
      title = right.join(" ") || header[i + 1]?.text || "";
    }
    title = title.replace(/^(?:PROJECT\s+NO\.?:?\s*[\w.\-/]+|ISSUED\s+(?:DATE|FOR):?\s*[\w\-]+)\s*/gi, "");
    return { num: secNum(m), title: title.replace(/\s+/g, " ").trim() };
  }
  return null;
}

/** pages → { sections: [{ num, title, trade, pageFrom, pageTo, chars }], chunks: [{ key, section, heading, part, page, text }] } */
export function splitSpec(pages) {
  // lines that repeat on many pages (project name, issue date, "Page 3 of 16"…) are page furniture
  const norm = (t) => t.replace(/\d+/g, "#").toLowerCase();
  const freq = new Map();
  for (const pg of pages) for (const l of new Set([...pg.header, ...pg.footer, ...pg.body.slice(0, 3)].map((x) => norm(x.text)))) freq.set(l, (freq.get(l) || 0) + 1);
  const furniture = (t) => (freq.get(norm(t)) || 0) >= Math.max(4, pages.length * 0.2) || /^page\s+\d+\s+of\s+\d+$/i.test(t);

  const sections = [];
  let cur = null;
  const open = (num, title, page) => {
    cur = sections.find((s) => s.num === num);
    if (!cur) sections.push((cur = { num, title: title || "", pageFrom: page, pageTo: page, lines: [] }));
    if (!cur.title && title) cur.title = title;
  };
  for (const pg of pages) {
    const h = headerSection(pg.header);
    let body = pg.body.filter((l) => !furniture(l.text));
    if (h) open(h.num, h.title, pg.page);
    else {
      // no running header: "SECTION 09 21 16" at the top of the page starts one
      const k = body.slice(0, 6).findIndex((l) => SEC_RE.test(l.text) && /^\s*SECTION/i.test(l.text));
      if (k >= 0) {
        const m = body[k].text.match(SEC_RE);
        const title = body[k].text.slice(body[k].text.search(SEC_RE) + m[0].length).trim() || body[k + 1]?.text || "";
        open(secNum(m), title, pg.page);
        body = body.slice(k + (body[k].text.slice(body[k].text.search(SEC_RE) + m[0].length).trim() ? 1 : 2));
      }
    }
    if (!cur) open("00 00 00", "Front matter", pg.page);
    cur.pageTo = pg.page;
    for (const l of body) cur.lines.push({ ...l, page: pg.page });
  }

  const chunks = [];
  const HEAD = /^(\d{1,2}\.\d{1,2})\s+([A-Z][A-Z0-9 ,.\-/&()'’:]{2,80})$/;
  const PART = /^PART\s+(\d)\b\s*[-–—]?\s*(.*)$/i;
  for (const s of sections) {
    s.trade = sectionTrade(s.num, s.title);
    let part = "";
    let c = null;
    const flush = () => {
      if (c && c.text.trim()) {
        // long articles → ~2,400-character pieces, cut at a paragraph
        const t = c.text.trim();
        if (t.length <= 2800) chunks.push({ ...c, text: t });
        else {
          let buf = "";
          let n = 0;
          for (const para of t.split(/\n(?=\s*(?:\d+\.\d+(?:\.\d+)*|\.\d+)\s)/)) {
            if (buf.length + para.length > 2400 && buf) {
              chunks.push({ ...c, key: `${c.key}-${n++}`, text: buf.trim() });
              buf = "";
            }
            buf += (buf ? "\n" : "") + para;
          }
          if (buf.trim()) chunks.push({ ...c, key: `${c.key}-${n}`, text: buf.trim() });
        }
      }
      c = null;
    };
    for (const l of s.lines) {
      const t = l.text;
      const pm = t.match(PART);
      if (pm) {
        flush();
        part = `PART ${pm[1]}${pm[2] ? ` ${pm[2]}` : ""}`.trim();
        continue;
      }
      const hm = t.match(HEAD);
      if (hm && !/END OF SECTION/i.test(t)) {
        flush();
        c = { key: `${s.num}|${hm[1]}`, section: s.num, heading: `${hm[1]} ${hm[2].trim()}`, part, page: l.page, text: "" };
        continue;
      }
      if (/^END OF SECTION$/i.test(t)) continue;
      if (!c) c = { key: `${s.num}|${part || "intro"}|${l.page}`, section: s.num, heading: part || s.title, part, page: l.page, text: "" };
      c.text += `${t}\n`;
    }
    flush();
    s.chars = s.lines.reduce((n, l) => n + l.text.length + 1, 0);
    delete s.lines;
  }
  // keys unique
  const seen = new Map();
  for (const c of chunks) {
    const n = seen.get(c.key) || 0;
    seen.set(c.key, n + 1);
    if (n) c.key = `${c.key}#${n}`;
  }
  // sections that talk about our work although they're someone else's (blocking, "by Section 09 21 16"…)
  for (const s of sections) {
    if (s.trade) continue;
    const txt = chunks.filter((c) => c.section === s.num).map((c) => c.text).join(" ");
    const hits = (txt.match(/09\s?21\s?16|09\s?22\s?16|gypsum board|drywall|steel stud|blocking|bulkhead|access panel|firestop/gi) || []).length;
    if (hits >= 2) {
      s.trade = "mentions";
      s.mentions = hits;
    }
  }
  return { sections: sections.filter((s) => s.chars > 0 || s.num !== "00 00 00"), chunks };
}

// project details from the first pages (title page) — best effort
export function specMeta(pages) {
  const first = (pages[0]?.body || []).map((l) => l.text);
  const all = pages.slice(0, 3).flatMap((p) => [...p.header, ...p.body]).map((l) => l.text);
  const pick = (re) => all.map((t) => t.match(re)).find(Boolean)?.[1]?.trim() || "";
  return {
    // the first real line of the title page ("393 UNIVERSITY AVE - 19TH FLOOR FIT UP")
    title: (first.find((t) => !/^(project manual|\(?specifications\)?|volume|issued|\d{4}-\d{2}-\d{2}|division)/i.test(t.trim()) && t.trim().length > 3) || "").trim().slice(0, 140),
    projectNo: pick(/PROJECT\s+NO\.?:?\s*([\w.\-/]+)/i),
    issuedFor: pick(/ISSUED\s+FOR:?\s*([A-Za-z ]{3,30})/i),
    issuedDate: pick(/(?:ISSUED\s+DATE:?|^)\s*(\d{4}-\d{2}-\d{2})/i),
  };
}

// ── search (no AI) ───────────────────────────────────────────────────────────
// trade-aware: "5/8 type x", "20ga studs", "level 5", "deflection track" … via the item search's vocabulary
export function buildSpecIndex(chunks = [], sections = []) {
  const trade = new Map(sections.map((s) => [s.num, s.trade]));
  const BOOST = { core: 1.6, related: 1.15, mentions: 1.05 };
  const docs = chunks.map((c) => {
    const body = analyzeText(c.text);
    return {
      c,
      head: analyzeText(`${c.heading}`),
      body,
      low: c.text.toLowerCase(),
      hasGa: [...body].some((t) => /^(ga|mil)\d/.test(t)),
      boost: BOOST[trade.get(c.section)] || 1,
    };
  });
  const df = new Map();
  for (const d of docs) for (const t of new Set([...d.head, ...d.body])) df.set(t, (df.get(t) || 0) + 1);
  return { docs, df, N: docs.length || 1 };
}

const GAUGE_WORDS = new Set(["gauge", "gage", "ga", "thickness", "thick", "mil", "mils"]);
/** → [{ chunk, score, snippet }] best first */
// words a question is full of that say nothing about the spec
const STOP = new Set("what which where when who how why are is was were the a an and or of for to in on at by with from does do did can could should would will shall be been any all there this that these those it its our we us you your required require requirement requirements specified spec section please tell me about need needed use used get".split(" "));
/** loose: for questions — no word is required, the more (and rarer) words an article has, the higher it ranks */
export function searchSpec(index, q, { sections = null, limit = 40, loose = false } = {}) {
  const query = String(q || "").trim();
  if (!index || !query) return [];
  let terms = analyzeQuery(query);
  if (loose) terms = terms.filter((t) => !STOP.has(t.raw) || t.canon);
  const phrase = query.toLowerCase().replace(/\s+/g, " ");
  const idf = (t) => Math.log(1 + index.N / (1 + (index.df.get(t) || 0)));
  const out = [];
  for (const d of index.docs) {
    if (sections && !sections.has(d.c.section)) continue;
    if (loose && /REFERENCES|DEFINITIONS/i.test(d.c.heading)) continue; // standards lists answer nothing
    let score = 0;
    let miss = 0;
    for (const t of terms) {
      let best = 0;
      for (const a of t.alts) {
        if (d.head.has(a)) best = Math.max(best, idf(a) * 2.5);
        else if (d.body.has(a)) best = Math.max(best, idf(a));
      }
      if (!best && GAUGE_WORDS.has(t.raw) && d.hasGa) best = 2; // "stud gauge" → "0.45 mm (25 ga)"
      if (!best && t.raw.length > 2 && d.low.includes(t.raw)) best = 0.5;
      if (best) score += best;
      else if (!t.soft) miss++;
    }
    const hard = terms.filter((t) => !t.soft).length;
    if (loose ? hard - miss < Math.max(1, Math.ceil(hard / 3)) : miss > (hard >= 2 ? 1 : 0) || miss >= hard) continue;
    if (phrase.length > 3 && d.low.includes(phrase)) score += 4;
    score = (score - (loose ? 0 : miss * 2)) * d.boost * (loose ? d.boost : 1);
    if (score > 0) out.push({ chunk: d.c, score });
  }
  out.sort((a, b) => b.score - a.score);
  return out.slice(0, limit).map((r) => ({ ...r, snippet: snippet(r.chunk.text, query) }));
}

// a few lines around the first hit
export function snippet(text, q, max = 260) {
  const words = String(q || "")
    .toLowerCase()
    .split(/\s+/)
    .filter((w) => w.length > 2);
  const low = text.toLowerCase();
  let at = -1;
  for (const w of words) {
    at = low.indexOf(w);
    if (at >= 0) break;
  }
  const from = Math.max(0, (at < 0 ? 0 : at) - 80);
  const s = text.slice(from, from + max).replace(/\s+/g, " ").trim();
  return `${from > 0 ? "…" : ""}${s}${from + max < text.length ? "…" : ""}`;
}

// ── things a drywall estimator usually has to ask about (no AI) ──────────────
// → [{ question, why, ref, page, section }]  stored with the spec, offered as RFI items
const CHECKS = [
  {
    re: /level\s*5\b/i,
    q: (s, h) => `Spec ${s.num} [${h}] calls for a Level 5 finish in some locations. Please identify on the drawings every wall and ceiling that requires Level 5.`,
    why: "Level 5 is priced separately — the locations are needed",
  },
  {
    re: /delegated|bearing the (?:seal|stamp)|stamp of a (?:licensed )?professional engineer/i,
    q: (s, h) => `Spec ${s.num} [${h}] requires engineered / stamped design for part of the framing. Please confirm which partitions and ceilings this applies to (heights, spans) and whether the engineering is by the drywall subcontractor.`,
    why: "Engineering and heavier framing cost money",
  },
  {
    re: /mock-?ups?/i,
    q: (s, h) => `Spec ${s.num} [${h}] requires a mock-up. Please confirm its location and extent, and whether it can be part of the finished work.`,
    why: "Mock-ups are extra labour",
  },
  {
    re: /shaft\s*wall/i,
    q: (s, h) => `Spec ${s.num} [${h}] includes shaft wall assemblies. Please confirm whether any are in this scope and, if so, their locations, ratings and ULC design numbers.`,
    why: "Shaft wall is a different system and price",
  },
  {
    re: /penetration barrier|security mesh|expanded[- ]metal/i,
    q: (s, h) => `Spec ${s.num} [${h}] mentions penetration-barrier / security mesh in partitions. Please identify the walls that require it.`,
    why: "Mesh locations are not on the plans",
  },
  {
    re: /abuse[- ]resistant|impact[- ]resistant|high[- ]impact/i,
    q: (s, h) => `Spec ${s.num} [${h}] lists abuse / impact-resistant board. Please identify where it is required.`,
    why: "Different board, different price",
  },
  {
    re: /(?:in-wall|concealed) reinforcing|blocking|backing plate/i,
    q: (s, h) => `Spec ${s.num} [${h}] makes in-wall blocking / reinforcing part of this work. Please provide the locations and mounting heights of the items that need it (millwork, accessories, wall-mounted equipment, TVs).`,
    why: "Blocking is priced per location",
  },
  {
    re: /alternate|unit price/i,
    q: (s, h) => `Spec ${s.num} [${h}] refers to alternates / unit prices. Please confirm the alternates the drywall price must include.`,
    why: "Alternates must be priced separately",
  },
];
export function specChecks(sections = [], chunks = []) {
  const out = [];
  const ours = sections.filter((s) => s.trade === "core");
  for (const ck of CHECKS) {
    for (const s of ours) {
      const all = chunks.filter((c) => c.section === s.num && !/REFERENCES|DEFINITIONS/i.test(c.heading) && ck.re.test(c.text));
      // the products / execution article says more than the general part
      const hit = all.find((c) => !/^1\./.test(c.heading)) || all[0];
      if (!hit) continue;
      const art = String(hit.heading).split(" ")[0];
      out.push({ question: ck.q(s, `§${art}`), why: ck.why, ref: `${s.num} §${art}`, page: hit.page, section: s.num });
      break; // once per check
    }
  }
  return out;
}
